import type { ReactNode } from "react";
import { AppHeader } from "./AppHeader.js";
import { useLocation } from "react-router-dom";
import { isRootTabRoute } from "./MobileTabBar.js";
import { DesktopShell } from "./DesktopShell.js";
import { MobileFooter } from "./MobileFooter.js";
import { MobileTabBar, MOBILE_TAB_BAR_HEIGHT } from "./MobileTabBar.js";
import { useViewport } from "../lib/viewport.js";

/**
 * Viewport-aware shell wrapper. At >=1024px renders DesktopShell with
 * sidebar + topbar + (optional) right rail. Below that, renders the
 * existing AppHeader-on-top mobile layout.
 *
 * Use this for routes we've ported to design parity. Routes that
 * still use AppHeader directly continue working — adoption is gradual.
 *
 * Per docs/PLAN-2026-05-26-ux-parity.md §2.4.5.
 */
export type AppShellProps = {
  children: ReactNode;
  /** Right rail content — desktop only; ignored on mobile. */
  rightRail?: ReactNode;
  /** Breadcrumb chips — desktop only. */
  crumbs?: ReactNode[];
  /** Hide the desktop sidebar (drill-focus mode). Ignored on mobile. */
  hideSidebar?: boolean;
  /** Top-right content for the mobile AppHeader. Ignored on desktop. */
  mobileHeaderRight?: ReactNode;
  /**
   * Hide the mobile AppHeader entirely. Use for drill-focus mode where
   * the route renders its own sticky DrillBar at the top.
   */
  hideMobileHeader?: boolean;
  /**
   * Hide the mobile footer (Privacy / Terms / Support links). The default
   * is to show the footer so the last card on a page has breathing room
   * and legal links are always one tap away. Drills set this to true so
   * the sticky bottom submit bar isn't double-bordered by the footer.
   */
  hideMobileFooter?: boolean;
  /**
   * Hide the mobile bottom tab bar. Defaults to following `hideMobileFooter`
   * so drill-focus routes (which own the bottom edge with a sticky submit
   * bar) drop the tab bar too. Full-chrome pages get it automatically.
   */
  hideMobileNav?: boolean;
};

export function AppShell({
  children,
  rightRail,
  crumbs,
  hideSidebar,
  mobileHeaderRight,
  hideMobileHeader,
  hideMobileFooter,
  hideMobileNav,
}: AppShellProps) {
  const viewport = useViewport();
  const { pathname } = useLocation();

  if (viewport === "desktop") {
    return (
      <DesktopShell
        rightRail={rightRail}
        crumbs={crumbs}
        hideSidebar={hideSidebar}
      >
        {children}
      </DesktopShell>
    );
  }

  // Default: show the tab bar unless we're in drill-focus mode (where the
  // footer is also hidden and the route owns the bottom edge).
  const showTabBar = hideMobileNav == null ? !hideMobileFooter : !hideMobileNav;

  return (
    <>
      {/* NO TOP STRIP ON A ROOT TAB SCREEN. The tab bar pinned below already
          says where you are, and the page renders its own heading, so a strip
          carrying a brand mark and an avatar was a second navigation bar on a
          390px screen that also has the browser's own UI above it.

          It stays on NESTED screens, where a top bar has a real job: somewhere
          to go back to, and a title for a screen the tab bar cannot name. */}
      {!hideMobileHeader && !isRootTabRoute(pathname) && (
        <AppHeader right={mobileHeaderRight} />
      )}
      {/* Pad the bottom so the fixed tab bar never covers the last card or
          the footer's legal links. The extra 16px gap keeps the last
          interactive control (e.g. the diagnostic's primary button) clear of
          the bar so it stays tappable, not flush against / under it. */}
      <div
        style={{
          paddingBottom: showTabBar
            ? `calc(${MOBILE_TAB_BAR_HEIGHT}px + 16px + env(safe-area-inset-bottom))`
            : undefined,
        }}
      >
        {children}
        {!hideMobileFooter && <MobileFooter />}
      </div>
      {showTabBar && <MobileTabBar />}
    </>
  );
}
